import * as React from "react"
import { AlertTriangle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/**
 * Shared ErrorState (SPEC FE-1 / R3b).
 *
 * One consistent error + retry block, styled to the metabus theme, replacing the
 * 4 ad-hoc error patterns (error-card-with-Retry / inline red text / toast-only /
 * nothing). Reads the EXISTING react-query isError/error - no new data wiring.
 */
export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  message?: string
  /** Optional retry handler - renders a Retry button (wire to query.refetch()). */
  onRetry?: () => void
}

export function ErrorState({
  title = "Something went wrong",
  message = "We couldn't load this data. Please try again.",
  onRetry,
  className,
  ...props
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-[rgba(239,68,68,0.32)] bg-[rgba(239,68,68,0.08)] px-6 py-10 text-center",
        className
      )}
      data-testid="error-state"
      {...props}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(239,68,68,0.12)] text-destructive">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <h3 className="mt-4 font-heading text-base font-semibold tracking-tight text-foreground">
        {title}
      </h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <Button variant="outline" className="mt-4" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  )
}
